/**
 * Profile Screen
 * User profile management and subscription (billing) settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, Alert, ActivityIndicator, TouchableOpacity, Switch, Linking, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAuthStore } from '../store/authStore';
import { subscriptionsApi, SubscriptionStatus } from '../api/endpoints/subscriptions';
import { notificationPreferencesApi, NotificationPreferences } from '../api/endpoints/notificationPreferences';
import { demoApi, DemoProfile } from '../api/endpoints/demo';
import { useDemoMode } from '../contexts/DemoModeContext';
import PaywallModal from '../components/common/PaywallModal';
import Button from '../components/common/Button';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import logger from '../utils/logger';

export default function ProfileScreen() {
  const navigation = useNavigation();
  const { user, logout } = useAuthStore();

  // Subscription state
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences | null>(null);
  const [isLoadingPrefs, setIsLoadingPrefs] = useState(true);

  // Demo mode state
  const { demoMode, profileName: currentDemoProfile, refreshDemoStatus, exitDemoMode } = useDemoMode();
  const [demoProfiles, setDemoProfiles] = useState<DemoProfile[]>([]);
  const [isLoadingDemoProfiles, setIsLoadingDemoProfiles] = useState(false);
  const [isActivatingDemo, setIsActivatingDemo] = useState(false);
  const [showProfileSelector, setShowProfileSelector] = useState(false);

  // Fetch subscription and notification preferences on mount
  useEffect(() => {
    fetchSubscription();
    fetchNotifPrefs();
  }, []);

  const fetchSubscription = async () => {
    try {
      setIsLoadingSubscription(true);
      const response = await subscriptionsApi.getCurrentSubscription();
      logger.log('[ProfileScreen] Subscription data:', JSON.stringify(response.subscription, null, 2));
      setSubscription(response.subscription);
    } catch (error) {
      logger.error('Error fetching subscription:', error);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const fetchNotifPrefs = async () => {
    try {
      setIsLoadingPrefs(true);
      const response = await notificationPreferencesApi.getPreferences();
      setNotifPrefs(response.preferences);
    } catch (error) {
      logger.error('Error fetching notification preferences:', error);
    } finally {
      setIsLoadingPrefs(false);
    }
  };

  const updateNotifPref = useCallback(async (update: Parameters<typeof notificationPreferencesApi.updatePreferences>[0]) => {
    // Optimistic update
    const previous = notifPrefs;
    if (notifPrefs) {
      const merged = { ...notifPrefs, ...update };
      if (update.notification_channels) {
        merged.notification_channels = { ...notifPrefs.notification_channels, ...update.notification_channels };
      }
      setNotifPrefs(merged as NotificationPreferences);
    }

    try {
      const response = await notificationPreferencesApi.updatePreferences(update);
      setNotifPrefs(response.preferences);
    } catch (error) {
      logger.error('Error updating notification preferences:', error);
      setNotifPrefs(previous);
    }
  }, [notifPrefs]);

  const DAYS_LABELS: Record<string, string> = {
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
  };

  const DAY_OPTIONS = Object.keys(DAYS_LABELS);

  const cycleDayForward = useCallback(() => {
    if (!notifPrefs) return;
    const currentIndex = DAY_OPTIONS.indexOf(notifPrefs.weekly_checkin_day);
    const nextIndex = (currentIndex + 1) % DAY_OPTIONS.length;
    updateNotifPref({ weekly_checkin_day: DAY_OPTIONS[nextIndex] });
  }, [notifPrefs, updateNotifPref]);

  const cycleTime = useCallback(() => {
    if (!notifPrefs) return;
    const hours = ['08:00', '09:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '21:00'];
    const currentIndex = hours.indexOf(notifPrefs.weekly_checkin_time);
    const nextIndex = (currentIndex + 1) % hours.length;
    updateNotifPref({ weekly_checkin_time: hours[nextIndex] });
  }, [notifPrefs, updateNotifPref]);

  // Demo mode functions
  const fetchDemoProfiles = async () => {
    try {
      setIsLoadingDemoProfiles(true);
      const profiles = await demoApi.getProfiles();
      setDemoProfiles(profiles);
    } catch (error) {
      logger.error('Error fetching demo profiles:', error);
    } finally {
      setIsLoadingDemoProfiles(false);
    }
  };

  const handleDemoToggle = async (enabled: boolean) => {
    if (enabled) {
      // Enabling demo mode - fetch profiles and show selector
      await fetchDemoProfiles();
      setShowProfileSelector(true);
    } else {
      // Disabling demo mode - exit demo
      await handleExitDemo();
    }
  };

  const handleSelectDemoProfile = async (profileKey: string) => {
    try {
      setIsActivatingDemo(true);
      await demoApi.activate(profileKey);
      await refreshDemoStatus();
      setShowProfileSelector(false);
    } catch (error: any) {
      logger.error('Error activating demo profile:', error);
      const errorMsg = error.response?.data?.error || 'Impossible d\'activer le mode démo';
      Platform.OS === 'web' ? window.alert(errorMsg) : Alert.alert('Erreur', errorMsg);
    } finally {
      setIsActivatingDemo(false);
    }
  };

  const handleExitDemo = async () => {
    try {
      await exitDemoMode();
      setShowProfileSelector(false);
    } catch (error: any) {
      logger.error('Error exiting demo mode:', error);
      const errorMsg = 'Impossible de quitter le mode démo';
      Platform.OS === 'web' ? window.alert(errorMsg) : Alert.alert('Erreur', errorMsg);
    }
  };

  const handleChangeProfile = async () => {
    await fetchDemoProfiles();
    setShowProfileSelector(true);
  };

  const isPremium = subscription?.is_premium || false;
  const isCanceled = !!subscription?.canceled_at;

  const getPlanLabel = () => {
    if (!subscription) return 'Gratuit';
    if (subscription.is_premium) {
      return subscription.plan === 'pro' ? 'Pro' : 'Premium';
    }
    return 'Gratuit';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleCancelSubscription = async () => {
    const confirmMessage = 'Es-tu sûr de vouloir annuler ton abonnement Premium ? Tu conserveras l\'accès jusqu\'à la fin de la période en cours.';

    // Use window.confirm on web, Alert.alert on native
    if (Platform.OS === 'web') {
      const confirmed = window.confirm(confirmMessage);
      if (!confirmed) return;

      setIsCanceling(true);
      try {
        await subscriptionsApi.cancelSubscription('User requested cancellation');
        window.alert('Ton abonnement a été annulé. Tu conserves l\'accès Premium jusqu\'à la fin de ta période de facturation.');
        fetchSubscription();
      } catch (error: any) {
        window.alert(error.message || 'Impossible d\'annuler l\'abonnement');
      } finally {
        setIsCanceling(false);
      }
    } else {
      Alert.alert(
        'Annuler l\'abonnement',
        confirmMessage,
        [
          { text: 'Non, garder', style: 'cancel' },
          {
            text: 'Oui, annuler',
            style: 'destructive',
            onPress: async () => {
              setIsCanceling(true);
              try {
                await subscriptionsApi.cancelSubscription('User requested cancellation');
                Alert.alert(
                  'Abonnement annulé',
                  'Ton abonnement a été annulé. Tu conserves l\'accès Premium jusqu\'à la fin de ta période de facturation.',
                );
                fetchSubscription();
              } catch (error: any) {
                Alert.alert('Erreur', error.message || 'Impossible d\'annuler l\'abonnement');
              } finally {
                setIsCanceling(false);
              }
            },
          },
        ],
      );
    }
  };

  const handleReactivateSubscription = async () => {
    const confirmMessage = 'Veux-tu reprendre ton abonnement Premium ? Il sera renouvelé automatiquement à la fin de la période en cours.';

    const doReactivate = async () => {
      setIsReactivating(true);
      try {
        const response = await subscriptionsApi.reactivateSubscription();
        const successMsg = response.message || 'Ton abonnement a été réactivé avec succès !';
        Platform.OS === 'web' ? window.alert(successMsg) : Alert.alert('Abonnement réactivé', successMsg);
        fetchSubscription();
      } catch (error: any) {
        const errorMsg = error.response?.data?.errors?.subscription?.[0] || error.message || 'Impossible de réactiver l\'abonnement';
        Platform.OS === 'web' ? window.alert(errorMsg) : Alert.alert('Erreur', errorMsg);
      } finally {
        setIsReactivating(false);
      }
    };

    if (Platform.OS === 'web') {
      const confirmed = window.confirm(confirmMessage);
      if (confirmed) {
        doReactivate();
      }
    } else {
      Alert.alert(
        'Reprendre l\'abonnement',
        confirmMessage,
        [
          { text: 'Non', style: 'cancel' },
          { text: 'Oui, reprendre', onPress: doReactivate },
        ],
      );
    }
  };

  const handleOpenPortal = async () => {
    try {
      const success = await subscriptionsApi.openPortal();
      if (!success) {
        const errorMsg = 'Impossible d\'ouvrir le portail de paiement';
        Platform.OS === 'web' ? window.alert(errorMsg) : Alert.alert('Erreur', errorMsg);
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Impossible d\'ouvrir le portail de paiement';
      Platform.OS === 'web' ? window.alert(errorMsg) : Alert.alert('Erreur', errorMsg);
    }
  };

  const handleUpgradeSuccess = () => {
    setShowPaywall(false);
    fetchSubscription();
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      {/* Profil Utilisateur */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Profil Utilisateur</Text>
        {user && (
          <View style={styles.card}>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Nom</Text>
              <Text style={styles.value}>
                {user.first_name && user.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user.username || '-'}
              </Text>
            </View>

            <View style={styles.divider} />

            <View style={styles.infoRow}>
              <Text style={styles.label}>Email</Text>
              <Text style={styles.value}>{user.email}</Text>
            </View>

            {user.phone_number && (
              <>
                <View style={styles.divider} />
                <View style={styles.infoRow}>
                  <Text style={styles.label}>Téléphone</Text>
                  <Text style={styles.value}>{user.phone_number}</Text>
                </View>
              </>
            )}

            <TouchableOpacity
              style={styles.editProfileButton}
              onPress={() => navigation.navigate('EditProfile' as never)}
            >
              <Icon name="account-edit-outline" size={18} color={colors.primary} />
              <Text style={styles.editProfileButtonText}>Modifier le profil</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Abonnement */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Abonnement</Text>

        {isLoadingSubscription ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator color={colors.primary} />
          </View>
        ) : (
          <>
            {/* Plan Info Card */}
            <View style={styles.card}>
              <View style={styles.planHeader}>
                <View style={styles.planTitleRow}>
                  <Icon
                    name={isPremium ? 'crown' : 'account-outline'}
                    size={24}
                    color={isPremium ? colors.primary : colors.textSecondary}
                  />
                  <Text style={[styles.planTitle, isPremium && styles.planTitlePremium]}>
                    {isPremium ? `Plan ${getPlanLabel()}` : 'Plan Gratuit'}
                  </Text>
                </View>
                {isPremium && (
                  <View style={[styles.badge, isCanceled && styles.badgeCanceled]}>
                    <Text style={[styles.badgeText, isCanceled && styles.badgeTextCanceled]}>
                      {isCanceled ? 'Annulé' : 'Actif'}
                    </Text>
                  </View>
                )}
              </View>

              <Text style={styles.planDescription}>
                {isPremium
                  ? 'Accès illimité à toutes les fonctionnalités'
                  : '3 messages/mois, 1 diagnostic, 1 objectif'}
              </Text>

              {isPremium && subscription?.current_period_end && (
                <View style={[styles.renewalInfo, isCanceled && styles.renewalInfoCanceled]}>
                  <Icon
                    name={isCanceled ? 'calendar-remove' : 'calendar-clock'}
                    size={18}
                    color={isCanceled ? colors.error : colors.primary}
                  />
                  <Text style={[styles.renewalText, isCanceled && styles.renewalTextCanceled]}>
                    {isCanceled
                      ? `Ton accès Premium prend fin le ${formatDate(subscription.current_period_end)}`
                      : `Renouvellement automatique le ${formatDate(subscription.current_period_end)}`}
                  </Text>
                </View>
              )}

              {!isPremium && (
                <TouchableOpacity
                  style={styles.upgradeButton}
                  onPress={() => setShowPaywall(true)}
                >
                  <Icon name="rocket-launch" size={18} color="#FFFFFF" />
                  <Text style={styles.upgradeButtonText}>Passer à Premium</Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Payment Method Card - Premium only */}
            {isPremium && (
              <View style={styles.card}>
                <View style={styles.cardHeader}>
                  <Icon name="credit-card-outline" size={20} color={colors.text} />
                  <Text style={styles.cardTitle}>Moyen de paiement</Text>
                </View>
                <Text style={styles.cardDescription}>
                  Géré via Stripe
                </Text>
                <TouchableOpacity style={styles.cardAction} onPress={handleOpenPortal}>
                  <Text style={styles.cardActionText}>Modifier le paiement</Text>
                  <Icon name="chevron-right" size={18} color={colors.primary} />
                </TouchableOpacity>
              </View>
            )}

            {/* Invoices Card - Premium only */}
            {isPremium && (
              <View style={styles.card}>
                <View style={styles.cardHeader}>
                  <Icon name="receipt" size={20} color={colors.text} />
                  <Text style={styles.cardTitle}>Factures</Text>
                </View>
                <Text style={styles.cardDescription}>
                  Vos factures sont disponibles dans le portail de paiement.
                </Text>
                <TouchableOpacity style={styles.cardAction} onPress={handleOpenPortal}>
                  <Text style={styles.cardActionText}>Voir les factures</Text>
                  <Icon name="chevron-right" size={18} color={colors.primary} />
                </TouchableOpacity>
              </View>
            )}

            {/* Cancellation Card - Premium only, not already canceled */}
            {isPremium && !isCanceled && (
              <View style={[styles.card, styles.cardDanger]}>
                <View style={styles.cardHeader}>
                  <Icon name="alert-circle-outline" size={20} color={colors.error} />
                  <Text style={[styles.cardTitle, styles.cardTitleDanger]}>Annulation</Text>
                </View>
                <Text style={styles.cardDescription}>
                  Vous conserverez l'accès Premium jusqu'à la fin de votre période de facturation.
                </Text>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={handleCancelSubscription}
                  disabled={isCanceling}
                >
                  {isCanceling ? (
                    <ActivityIndicator color={colors.error} size="small" />
                  ) : (
                    <>
                      <Icon name="credit-card-off-outline" size={18} color={colors.error} />
                      <Text style={styles.cancelButtonText}>Annuler l'abonnement</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            )}

            {/* Reactivation Card - Premium canceled, can reactivate before period end */}
            {isPremium && isCanceled && (
              <View style={[styles.card, styles.cardSuccess]}>
                <View style={styles.cardHeader}>
                  <Icon name="refresh" size={20} color={colors.success} />
                  <Text style={[styles.cardTitle, styles.cardTitleSuccess]}>Reprendre l'abonnement</Text>
                </View>
                <Text style={styles.cardDescription}>
                  Tu as changé d'avis ? Réactive ton abonnement pour continuer à profiter de toutes les fonctionnalités Premium.
                </Text>
                <TouchableOpacity
                  style={styles.reactivateButton}
                  onPress={handleReactivateSubscription}
                  disabled={isReactivating}
                >
                  {isReactivating ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <>
                      <Icon name="check-circle" size={18} color="#FFFFFF" />
                      <Text style={styles.reactivateButtonText}>Reprendre l'abonnement</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            )}
          </>
        )}
      </View>

      {/* Notifications */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Notifications</Text>

        {isLoadingPrefs ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator color={colors.primary} />
          </View>
        ) : notifPrefs ? (
          <>
            {/* Notification Channels */}
            <View style={styles.card}>
              <Text style={styles.notifGroupTitle}>Canaux de notification</Text>

              <View style={styles.notifRow}>
                <View style={styles.notifRowLeft}>
                  <Icon name="cellphone" size={20} color={colors.text} />
                  <Text style={styles.notifLabel}>Push notifications</Text>
                </View>
                <Switch
                  value={notifPrefs.notification_channels.push}
                  onValueChange={(value) => updateNotifPref({ notification_channels: { push: value } })}
                  trackColor={{ false: colors.border, true: `${colors.primary}80` }}
                  thumbColor={notifPrefs.notification_channels.push ? colors.primary : '#f4f3f4'}
                />
              </View>

              <View style={styles.divider} />

              <View style={styles.notifRow}>
                <View style={styles.notifRowLeft}>
                  <Icon name="email-outline" size={20} color={colors.text} />
                  <Text style={styles.notifLabel}>Email</Text>
                </View>
                <Switch
                  value={notifPrefs.notification_channels.email}
                  onValueChange={(value) => updateNotifPref({ notification_channels: { email: value } })}
                  trackColor={{ false: colors.border, true: `${colors.primary}80` }}
                  thumbColor={notifPrefs.notification_channels.email ? colors.primary : '#f4f3f4'}
                />
              </View>
            </View>

            {/* Wealfi Messages */}
            <View style={styles.card}>
              <Text style={styles.notifGroupTitle}>Messages Wealfi</Text>

              <View style={styles.notifRow}>
                <View style={styles.notifRowLeft}>
                  <Icon name="calendar-check" size={20} color={colors.text} />
                  <Text style={styles.notifLabel}>Bilan hebdomadaire</Text>
                </View>
                <Switch
                  value={notifPrefs.weekly_checkin_enabled}
                  onValueChange={(value) => updateNotifPref({ weekly_checkin_enabled: value })}
                  trackColor={{ false: colors.border, true: `${colors.primary}80` }}
                  thumbColor={notifPrefs.weekly_checkin_enabled ? colors.primary : '#f4f3f4'}
                />
              </View>

              {notifPrefs.weekly_checkin_enabled && (
                <View style={styles.subSettings}>
                  <TouchableOpacity style={styles.subSettingRow} onPress={cycleDayForward}>
                    <Text style={styles.subSettingLabel}>Jour</Text>
                    <View style={styles.subSettingValue}>
                      <Text style={styles.subSettingValueText}>
                        {DAYS_LABELS[notifPrefs.weekly_checkin_day] || notifPrefs.weekly_checkin_day}
                      </Text>
                      <Icon name="chevron-right" size={16} color={colors.textSecondary} />
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity style={styles.subSettingRow} onPress={cycleTime}>
                    <Text style={styles.subSettingLabel}>Heure</Text>
                    <View style={styles.subSettingValue}>
                      <Text style={styles.subSettingValueText}>{notifPrefs.weekly_checkin_time}</Text>
                      <Icon name="chevron-right" size={16} color={colors.textSecondary} />
                    </View>
                  </TouchableOpacity>
                </View>
              )}

              <View style={styles.divider} />

              <View style={styles.notifRow}>
                <View style={styles.notifRowLeft}>
                  <Icon name="bell-ring-outline" size={20} color={colors.text} />
                  <Text style={styles.notifLabel}>Rappels d'inactivité</Text>
                </View>
                <Switch
                  value={notifPrefs.inactivity_nudge_enabled}
                  onValueChange={(value) => updateNotifPref({ inactivity_nudge_enabled: value })}
                  trackColor={{ false: colors.border, true: `${colors.primary}80` }}
                  thumbColor={notifPrefs.inactivity_nudge_enabled ? colors.primary : '#f4f3f4'}
                />
              </View>

              <View style={styles.divider} />

              <View style={styles.notifRow}>
                <View style={styles.notifRowLeft}>
                  <Icon name="school-outline" size={20} color={colors.text} />
                  <Text style={styles.notifLabel}>Contenu éducatif</Text>
                </View>
                <Switch
                  value={notifPrefs.education_content_enabled}
                  onValueChange={(value) => updateNotifPref({ education_content_enabled: value })}
                  trackColor={{ false: colors.border, true: `${colors.primary}80` }}
                  thumbColor={notifPrefs.education_content_enabled ? colors.primary : '#f4f3f4'}
                />
              </View>
            </View>
          </>
        ) : null}
      </View>

      {/* Mode Démo */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Mode Démo</Text>

        <View style={styles.card}>
          <View style={styles.notifRow}>
            <View style={styles.notifRowLeft}>
              <Icon name="flask-outline" size={20} color={demoMode ? '#E65100' : colors.text} />
              <Text style={styles.notifLabel}>Activer le mode démo</Text>
            </View>
            <Switch
              value={demoMode}
              onValueChange={handleDemoToggle}
              trackColor={{ false: colors.border, true: '#FFB74D' }}
              thumbColor={demoMode ? '#E65100' : '#f4f3f4'}
            />
          </View>

          {demoMode && currentDemoProfile && !showProfileSelector && (
            <>
              <View style={styles.divider} />
              <View style={styles.demoActiveInfo}>
                <View style={styles.demoActiveRow}>
                  <Text style={styles.demoActiveLabel}>Profil actif</Text>
                  <Text style={styles.demoActiveValue}>{currentDemoProfile}</Text>
                </View>
                <TouchableOpacity style={styles.changeProfileButton} onPress={handleChangeProfile}>
                  <Icon name="swap-horizontal" size={16} color={colors.primary} />
                  <Text style={styles.changeProfileText}>Changer de profil</Text>
                </TouchableOpacity>
              </View>
            </>
          )}

          {showProfileSelector && (
            <>
              <View style={styles.divider} />
              <Text style={styles.demoSelectorTitle}>Choisir un profil fictif</Text>

              {isLoadingDemoProfiles ? (
                <View style={styles.demoLoadingContainer}>
                  <ActivityIndicator color={colors.primary} size="small" />
                </View>
              ) : (
                <View style={styles.demoProfilesList}>
                  {demoProfiles.map((profile) => (
                    <TouchableOpacity
                      key={profile.key}
                      style={[
                        styles.demoProfileItem,
                        currentDemoProfile === profile.name && styles.demoProfileItemActive,
                      ]}
                      onPress={() => handleSelectDemoProfile(profile.key)}
                      disabled={isActivatingDemo}
                    >
                      <View style={styles.demoProfileHeader}>
                        <Text style={styles.demoProfileEmoji}>{profile.emoji}</Text>
                        <View style={styles.demoProfileInfo}>
                          <Text style={styles.demoProfileName}>{profile.name}</Text>
                          <Text style={styles.demoProfileTagline}>{profile.tagline}</Text>
                        </View>
                        {currentDemoProfile === profile.name && (
                          <Icon name="check-circle" size={20} color={colors.success} />
                        )}
                      </View>
                      {isActivatingDemo && (
                        <ActivityIndicator
                          color={colors.primary}
                          size="small"
                          style={styles.demoProfileLoader}
                        />
                      )}
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              <TouchableOpacity
                style={styles.demoCancelButton}
                onPress={() => setShowProfileSelector(false)}
              >
                <Text style={styles.demoCancelText}>Annuler</Text>
              </TouchableOpacity>
            </>
          )}
        </View>

        <Text style={styles.demoDescription}>
          Le mode démo permet de tester Wealfi avec des données fictives sans importer vos vrais relevés bancaires.
        </Text>
      </View>

      {/* Déconnexion */}
      <Button
        title="Se déconnecter"
        onPress={handleLogout}
        variant="outline"
        style={styles.logoutButton}
      />

      {/* Paywall Modal */}
      <PaywallModal
        visible={showPaywall}
        onClose={() => setShowPaywall(false)}
        onSubscribeSuccess={handleUpgradeSuccess}
        trigger="feature"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    padding: spacing.lg,
    paddingBottom: spacing.xxl,
  },
  section: {
    marginBottom: spacing.xl,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  card: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  cardDanger: {
    borderWidth: 1,
    borderColor: `${colors.error}30`,
  },
  cardSuccess: {
    borderWidth: 1,
    borderColor: `${colors.success}30`,
    backgroundColor: `${colors.success}08`,
  },
  loadingContainer: {
    padding: spacing.xl,
    alignItems: 'center',
  },
  infoRow: {
    paddingVertical: spacing.sm,
  },
  label: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  value: {
    fontSize: typography.fontSize.md,
    color: colors.text,
    fontWeight: typography.fontWeight.medium,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: spacing.xs,
  },
  editProfileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: 8,
    marginTop: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  editProfileButtonText: {
    color: colors.primary,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
  },
  logoutButton: {
    marginTop: spacing.lg,
  },

  // Plan Card Styles
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  planTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  planTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  planTitlePremium: {
    color: colors.primary,
  },
  badge: {
    backgroundColor: `${colors.success}20`,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 12,
  },
  badgeCanceled: {
    backgroundColor: `${colors.error}20`,
  },
  badgeText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    color: colors.success,
  },
  badgeTextCanceled: {
    color: colors.error,
  },
  planDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  renewalInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    marginTop: spacing.sm,
    backgroundColor: `${colors.primary}10`,
    borderRadius: 8,
  },
  renewalInfoCanceled: {
    backgroundColor: `${colors.error}10`,
  },
  renewalText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
  },
  renewalTextCanceled: {
    color: colors.error,
  },
  upgradeButton: {
    backgroundColor: colors.primary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: 8,
    marginTop: spacing.sm,
  },
  upgradeButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },

  // Card Styles
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  cardTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  cardTitleDanger: {
    color: colors.error,
  },
  cardTitleSuccess: {
    color: colors.success,
  },
  cardDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  cardAction: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  cardActionText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
  },

  // Cancel Button
  cancelButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.error,
  },
  cancelButtonText: {
    color: colors.error,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
  },

  // Notification Styles
  notifGroupTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: spacing.md,
  },
  notifRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    paddingVertical: spacing.sm,
  },
  notifRowLeft: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: spacing.sm,
    flex: 1,
  },
  notifLabel: {
    fontSize: typography.fontSize.md,
    color: colors.text,
    fontWeight: typography.fontWeight.medium,
  },
  subSettings: {
    marginLeft: 28 + spacing.sm,
    marginBottom: spacing.sm,
  },
  subSettingRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    paddingVertical: spacing.xs,
  },
  subSettingLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  subSettingValue: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: spacing.xs,
  },
  subSettingValueText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
  },

  // Reactivate Button
  reactivateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: 8,
    backgroundColor: colors.success,
  },
  reactivateButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },

  // Demo Mode Styles
  demoDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: spacing.sm,
    lineHeight: 18,
  },
  demoActiveInfo: {
    paddingTop: spacing.sm,
  },
  demoActiveRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    marginBottom: spacing.sm,
  },
  demoActiveLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  demoActiveValue: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: '#E65100',
  },
  changeProfileButton: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: spacing.xs,
    alignSelf: 'flex-start' as const,
  },
  changeProfileText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
  },
  demoSelectorTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  demoLoadingContainer: {
    padding: spacing.lg,
    alignItems: 'center' as const,
  },
  demoProfilesList: {
    gap: spacing.sm,
  },
  demoProfileItem: {
    backgroundColor: colors.background,
    borderRadius: 10,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  demoProfileItemActive: {
    borderColor: colors.success,
    backgroundColor: `${colors.success}08`,
  },
  demoProfileHeader: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
  },
  demoProfileEmoji: {
    fontSize: 28,
    marginRight: spacing.sm,
  },
  demoProfileInfo: {
    flex: 1,
  },
  demoProfileName: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  demoProfileTagline: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  demoProfileLoader: {
    marginTop: spacing.sm,
  },
  demoCancelButton: {
    alignItems: 'center' as const,
    paddingVertical: spacing.md,
    marginTop: spacing.sm,
  },
  demoCancelText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium,
  },
});
